/* global theme, Shopify */
/**
 * Adds or removes the free-gift variant based on theme editor settings.
 * The gift price is excluded from the qualifying cart amount.
 */
(function () {
  const config = window.theme && window.theme.giftWithPurchase;
  if (!config || !config.variantId) return;

  const MARKER = config.marker || 'auto-gift';
  const STORAGE_KEY = `aruelle-auto-gift:${config.variantId}`;
  const ZERO_DECIMAL = {
    BIF: 1, CLP: 1, DJF: 1, GNF: 1, JPY: 1, KMF: 1, KRW: 1, MGA: 1,
    PYG: 1, RWF: 1, UGX: 1, VND: 1, VUV: 1, XAF: 1, XOF: 1, XPF: 1
  };

  const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  const originalFetch = window.fetch.bind(window);
  let ownDepth = 0;
  let running = false;
  let queued = false;
  let soldOut = false;
  let timer = 0;

  function requestUrl(input) {
    if (typeof input === 'string') return input;
    if (input && typeof input.url === 'string') return input.url;
    return '';
  }

  function isCartMutation(input) {
    return /\/cart\/(add|change|update)(\.js)?([/?#]|$)/.test(requestUrl(input));
  }

  function withRoot(path) {
    const base = root.endsWith('/') ? root.slice(0, -1) : root;
    return `${base}/${String(path).replace(/^\//, '')}`;
  }

  window.fetch = function (input, init) {
    const result = originalFetch(input, init);
    if (isCartMutation(input)) {
      result.then((response) => {
        if (!response || !response.ok) return;
        if (ownDepth > 0) queued = true;
        else scheduleSync();
      }).catch(() => {});
    }
    return result;
  };

  function ownFetch(url, options) {
    ownDepth += 1;
    return originalFetch(url, options).finally(() => {
      ownDepth -= 1;
    });
  }

  function propertyValue(item, name) {
    const props = item && item.properties;
    if (!props) return '';
    if (Array.isArray(props)) {
      for (let i = 0; i < props.length; i += 1) {
        const entry = props[i];
        if (!entry) continue;
        if (entry.name === name || entry.first === name || entry[0] === name) {
          return entry.value || entry.last || entry[1] || '';
        }
      }
      return '';
    }
    return props[name] || '';
  }

  function isAutoGift(item) {
    return String(propertyValue(item, '_auto_gift')) === MARKER;
  }

  function parseRules(raw) {
    return String(raw || '')
      .split(/\n/)
      .map((line) => line.trim())
      .filter(Boolean)
      .map((line) => {
        const parts = line.split(':').map((part) => part.trim()).filter(Boolean);
        if (parts.length >= 2 && /^[A-Za-z]{3}$/.test(parts[0])) {
          return { currency: parts[0].toUpperCase(), amount: parts[parts.length - 1] };
        }
        return { currency: '*', amount: parts[0] };
      });
  }

  const rules = parseRules(config.threshold);

  function toMinor(amount, currency) {
    const value = Number(String(amount).replace(/\s/g, '').replace(',', '.'));
    if (!Number.isFinite(value) || value < 0) return null;
    return Math.round(value * (ZERO_DECIMAL[currency] ? 1 : 100));
  }

  function thresholdFor(currency) {
    const code = String(currency || '').toUpperCase();
    const specific = rules.find((rule) => rule.currency === code);
    if (specific) return toMinor(specific.amount, code);

    const shopCode = String(config.shopCurrency || '').toUpperCase();
    const shopRule = rules.find((rule) => rule.currency === shopCode);
    const rate = Number(window.Shopify && Shopify.currency && Shopify.currency.rate);
    if (shopRule && code && code !== shopCode && Number.isFinite(rate) && rate > 0) {
      const shopMinor = toMinor(shopRule.amount, shopCode);
      if (shopMinor !== null) return Math.round(shopMinor * rate);
    }

    const fallback = rules.find((rule) => rule.currency === '*');
    if (!fallback) return null;
    return toMinor(fallback.amount, code);
  }

  function isGiftVariant(item) {
    return Number(item.variant_id) === Number(config.variantId);
  }

  function isGiftCardProduct(item) {
    if (item.gift_card) return true;
    const type = String(item.product_type || '').toLowerCase();
    return type === 'gift card' || type === 'gift cards';
  }

  function isPhysicalProduct(item) {
    if (!item || isGiftVariant(item) || isAutoGift(item) || isGiftCardProduct(item)) return false;
    return item.requires_shipping === true;
  }

  function qualifyingAmount(cart) {
    const field = config.count === 'after_discounts' ? 'final_line_price' : 'original_line_price';
    return cart.items.reduce((sum, item) => {
      if (isGiftVariant(item) || isAutoGift(item)) return sum;
      const price = item[field];
      return sum + (typeof price === 'number' ? price : 0);
    }, 0);
  }

  function remember(state) {
    try {
      if (state) sessionStorage.setItem(STORAGE_KEY, state);
      else sessionStorage.removeItem(STORAGE_KEY);
    } catch (error) {
      /* sessionStorage can be blocked */
    }
  }

  function remembered() {
    try {
      return sessionStorage.getItem(STORAGE_KEY) || '';
    } catch (error) {
      return '';
    }
  }

  async function readCart() {
    const response = await ownFetch(withRoot('cart.js'), { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error('Cart could not be read');
    return response.json();
  }

  async function writeCart(url, body) {
    const response = await ownFetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify(body)
    });
    if (!response.ok) {
      const error = new Error('Cart could not be updated');
      error.status = response.status;
      throw error;
    }
    return response.json();
  }

  function lockGiftQuantities(giftLines, updates) {
    if (!config.lockQuantity) return;
    giftLines.forEach((line, index) => {
      const quantity = index === 0 ? 1 : 0;
      if (line.quantity !== quantity) updates[line.key] = quantity;
    });
  }

  async function refreshUi() {
    if (!window.theme || !theme.mergeNodes) return;

    const targets = Array.from(document.querySelectorAll('cart-form[data-section-id], page-header[data-section-id]'));
    const ids = [];
    targets.forEach((el) => {
      if (el.dataset.sectionId && ids.indexOf(el.dataset.sectionId) === -1) ids.push(el.dataset.sectionId);
    });
    if (!ids.length) return;

    const response = await ownFetch(`${withRoot('')}?sections=${ids.join(',')}`, {
      headers: { Accept: 'application/json' }
    });
    if (!response.ok) return;
    const data = await response.json();

    targets.forEach((el) => {
      const html = data[el.dataset.sectionId];
      if (!html) return;

      if (el.tagName === 'PAGE-HEADER' && typeof el.updateFromCartChange === 'function') {
        el.updateFromCartChange(html);
        return;
      }

      const wrapper = document.createElement('div');
      wrapper.innerHTML = html;
      wrapper.querySelectorAll('[data-cc-animate]').forEach((node) => node.removeAttribute('data-cc-animate'));
      theme.mergeNodes(wrapper, el);
      el.classList.remove('cart-form--refreshing');
      el.querySelectorAll('.merge-item-refreshing').forEach((node) => node.classList.remove('merge-item-refreshing'));
      el.dispatchEvent(new CustomEvent('on:cart:after-merge', { bubbles: true, cancelable: false }));
    });
  }

  async function reconcile() {
    const cart = await readCart();
    const giftLines = cart.items.filter(isAutoGift);
    const threshold = thresholdFor(cart.currency);
    const behavior = config.behavior || 'add_and_remove';
    const updates = {};
    let added = false;

    if (!config.enabled) {
      giftLines.forEach((line) => {
        updates[line.key] = 0;
      });
      remember('');
    } else if (threshold === null) {
      return false;
    } else if (!cart.items.some(isPhysicalProduct) || qualifyingAmount(cart) < threshold) {
      if (behavior !== 'add_only') {
        giftLines.forEach((line) => {
          updates[line.key] = 0;
        });
        remember('');
      }
    } else if (behavior === 'remove_only') {
      lockGiftQuantities(giftLines, updates);
    } else {
      const manualSameVariant = cart.items.some((item) => (
        Number(item.variant_id) === Number(config.variantId) && !isAutoGift(item)
      ));

      if (giftLines.length) {
        remember('present');
        lockGiftQuantities(giftLines, updates);
      } else if (!manualSameVariant) {
        const state = remembered();
        if (state === 'present' && config.ifRemoved !== 'restore') {
          remember('dismissed');
        } else if (!(state === 'dismissed' && config.ifRemoved !== 'restore') && !soldOut) {
          added = true;
        }
      }
    }

    const hasUpdates = Object.keys(updates).length > 0;
    if (!added && !hasUpdates) return false;

    if (hasUpdates) {
      await writeCart(withRoot('cart/update.js'), { updates });
    }

    if (added) {
      try {
        await writeCart(theme.routes.cartAdd || withRoot('cart/add.js'), {
          items: [{
            id: config.variantId,
            quantity: 1,
            properties: { _auto_gift: MARKER }
          }]
        });
        remember('present');
      } catch (error) {
        if (error.status === 422) soldOut = true;
        throw error;
      }
    }

    return true;
  }

  async function sync() {
    if (running) {
      queued = true;
      return;
    }

    running = true;
    try {
      do {
        queued = false;
        let changed = false;
        try {
          changed = await reconcile();
        } catch (error) {
          console.warn('Free gift cart update failed', error);
          queued = false;
        }

        if (changed) {
          try {
            await refreshUi();
          } catch (error) {
            console.warn('Free gift cart refresh failed', error);
          }
        }
      } while (queued);
    } finally {
      running = false;
    }
  }

  function scheduleSync() {
    window.clearTimeout(timer);
    timer = window.setTimeout(sync, 300);
  }

  function syncNow() {
    window.clearTimeout(timer);
    return sync();
  }

  document.addEventListener('click', (event) => {
    if (event.defaultPrevented) return;

    const trigger = event.target.closest('a[name="checkout"], button[name="checkout"]');
    if (!trigger || trigger.dataset.giftCheckout === '1') return;

    const href = trigger.tagName === 'A' ? trigger.href : '';
    const formId = trigger.form ? trigger.form.id : '';
    event.preventDefault();

    const checkoutDelay = new Promise((resolve) => {
      window.setTimeout(resolve, 2500);
    });

    Promise.race([syncNow(), checkoutDelay]).finally(() => {
      if (href) {
        window.location.assign(href);
        return;
      }

      const form = (formId && document.getElementById(formId)) || trigger.form;
      const again = form && form.querySelector('[name="checkout"]');
      if (!again) return;
      again.dataset.giftCheckout = '1';
      again.click();
    });
  });

  document.addEventListener('on:cart:change', scheduleSync);
  document.addEventListener('on:cart:add', scheduleSync);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scheduleSync);
  } else {
    scheduleSync();
  }
}());
